import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import {
	ProductComplianceTechnicalHeadingTypeEnum,
	ThemeFontStyleTypeEnum
} from '@inter-ikea-kompis/enums';
import type {
	ILocalizedInformation,
	IProduct,
	IProductComplianceLabel,
	ITheme
} from '@inter-ikea-kompis/types';
import { ProductUtility } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { HyperlinkColorEnum } from '@inter-ikea-kompis/component-hyperlink';
import { Hyperlink } from '@inter-ikea-kompis/component-hyperlink';
import { QrCode } from '@inter-ikea-kompis/component-qr-code';
import { Text } from '@inter-ikea-kompis/component-text';
import { TextToggle } from '@inter-ikea-kompis/component-text-toggle';

import type IEnergyEfficiencyClassTranslations from '../../types/IEnergyEfficiencyClassTranslations.js';
import EprelQrToggle from '../eprel-qr-toggle/EprelQrToggle.js';
import EnergyLabelCardStyle from './EnergyLabelCardStyle.js';

const LABEL_FILE_TYPE = 'svg';

/**
 * Data-driven information listing.
 */

export default class EnergyLabelCard extends KompisElement {
	// A product.
	@property({ type: Object, required: true })
	public product!: IProduct;

	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated - use "translations" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IEnergyEfficiencyClassTranslations | null = null;

	// Disables links. This attribute should be set to "true" when in a Kiosk.
	@property({ type: Boolean })
	public disableLinks = false;

	// Filter the energy labels to be shown. By default, all labels are visible.
	@property({ type: Object })
	public labelFilter: IProductComplianceLabel[] | null = null;

	private expandedTextToggle = false;

	/**
	 * @returns Template.
	 */
	public override render(): RenderResult {
		// We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslations()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		const rescaled = !!ProductUtility.getProductInformationSheetLink(this.product);
		const energyType = ProductUtility.getProductComplianceType(this.product);

		return html`
			<style>
				${EnergyLabelCardStyle.getStyle({
					theme: this.theme,
					rescaled,
					energyType
				})}
			</style>
			<div class="container">${this.getCard()}</div>
		`;
	}

	public override connectedCallback(): void {
		this.customElements.define('kompis-hyperlink', Hyperlink);
		this.customElements.define('kompis-qr-code', QrCode);
		this.customElements.define('kompis-text-toggle', TextToggle);
		this.customElements.define('kompis-text', Text);
		this.customElements.define('kompis-eprel-qr-toggle', EprelQrToggle);

		super.connectedCallback();
	}

	private hasTranslations(): boolean {
		return !!this.translations || !!this.localizedInformation?.translations;
	}
	/**
	 * Returns either Eprel card or Fiche card depending on what data comes from DEXF.
	 */
	private getCard(): RenderResult {
		return html`
			<div class="labels">${this.getLabels()}</div>
			${this.getEprelLink()}
		`;
	}

	private getLabels(): RenderResult {
		return this.getLabelUrls().map((url) => html`<img src="${url}" alt="${this.getImageAlt()}" />`);
	}

	/**
	 * Return url to the label SVGs.
	 */
	private getLabelUrls(): string[] {
		if (this.product.content?.complianceLabel?.length) {
			return this.product.content.complianceLabel
				.filter((label) => {
					if (this.labelFilter) {
						return this.labelFilter.includes(label);
					}
					return true;
				})
				.filter((label) => label.fileType === LABEL_FILE_TYPE)
				.map((label) => label.url);
		}
		return [];
	}

	/**
	 * Returns corresponding alt text for the energy efficiency class image.
	 */
	private getImageAlt(): string {
		const complianceLabelType = ProductUtility.getProductComplianceType(this.product);

		switch (complianceLabelType) {
			case ProductComplianceTechnicalHeadingTypeEnum.energyEfficiencyClass: {
				const eecObject = this.product?.content?.complianceTechnical?.find(
					(obj) => obj.headingType === 'ENERGY_EFFICIENCY_CLASS'
				);
				if (!eecObject) {
					return '';
				}

				const { headingText, text } = eecObject;
				return `${headingText} ${text}`;
			}
			case ProductComplianceTechnicalHeadingTypeEnum.hyperlink: {
				return this.product?.content?.complianceLabel?.find((obj) => obj.labelType)?.typeName ?? '';
			}
			case ProductComplianceTechnicalHeadingTypeEnum.nonRescaled: {
				const eecObject = this.product?.content?.technicalInformation?.find(
					(obj) => obj.typeCode === '00026'
				);
				if (!eecObject) {
					return '';
				}

				const { typeName, valueMetric } = eecObject;
				return `${typeName} ${valueMetric}`;
			}
			default: {
				return '';
			}
		}
	}

	/**
	 * Get EPREL link.
	 */
	private getEprelLink(): RenderResult {
		const link = this.product ? ProductUtility.getProductInformationSheetLink(this.product) : null;
		if (!link) {
			return null;
		}

		if (this.disableLinks) {
			return html`<kompis-eprel-qr-toggle
				.theme="${this.theme}"
				.product="${this.product}"
				.expanded="${this.expandedTextToggle}"
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				@animationEnd="${(
					event: CustomEvent & {
						target: HTMLElement;
					}
				) => {
					if (this.expandedTextToggle) {
						event.target.scrollIntoView({
							behavior: 'smooth'
						});
					}
				}}"
				@expand="${(event: CustomEvent) => {
					this.expandedTextToggle = event.detail.expanded;
					this.update();
				}}"
			></kompis-eprel-qr-toggle>`;
		}
		return html` <kompis-text
			class="eprelLink"
			.theme="${this.theme}"
			.type="${ThemeFontStyleTypeEnum.bodySmall}"
		>
			<kompis-hyperlink
				.target="${'_blank'}"
				.href="${link.href}"
				.theme="${this.theme}"
				.color="${HyperlinkColorEnum.grey}"
				.text="${link.text}"
				.underline="${true}"
			></kompis-hyperlink>
		</kompis-text>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-energy-label-card': EnergyLabelCard;
	}
}
